import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, User } from '../../types';
// import axios from 'axios'; // 暂时注释掉未使用的导入
import api from '../../utils/api';

// 🔒 安全修复：移除默认用户和模拟数据，所有认证必须通过后端验证

// Define the initial state
// 🔒 安全修复：不能仅根据localStorage有token就判断已认证
// 必须等待getUserProfile验证成功后才设置isAuthenticated=true
const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false, // 默认未认证，需要验证token有效性
  isLoading: !!localStorage.getItem('token'), // 如果有token，标记为加载中
  error: null,
};

// Login thunk
// 🔒 安全修复：移除所有模拟数据和fallback逻辑，强制通过后端认证
export const login = createAsyncThunk(
  'auth/login',
  async ({ username, password }: { username: string; password: string }, { rejectWithValue }) => {
    try {
      // 调用真实的后端API
      const response = await api.post('/auth/login', { username, password, clientType: 'web' });

      // 验证响应数据
      if (!response.data || !response.data.token || !response.data.user) {
        return rejectWithValue('服务器返回的数据格式不正确');
      }

      // Store token in localStorage
      localStorage.setItem('token', response.data.token);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '登录失败，请检查用户名和密码');
    }
  }
);

// Logout thunk
export const logout = createAsyncThunk('auth/logout', async () => {
  localStorage.removeItem('token');
  return null;
});

// Get user profile thunk
// 🔒 安全修复：移除fallback逻辑，token验证失败必须返回rejected
export const getUserProfile = createAsyncThunk('auth/getUserProfile', async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      return rejectWithValue('未找到token');
    }

    try {
      const response = await api.get('/auth/profile');

      // 验证响应数据
      if (!response.data) {
        return rejectWithValue('服务器返回的用户数据为空');
      }

      return response.data;
    } catch (error: any) {
      // 🔒 关键修复：API调用失败时，必须返回rejected，不能使用默认用户
      if (error.response?.status === 401) {
        return rejectWithValue('身份验证失败，请重新登录');
      }

      return rejectWithValue(error.response?.data?.message || '获取用户信息失败');
    }
  } catch (error: any) {
    return rejectWithValue('获取用户信息时发生错误');
  }
});

// Create the slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<{ user: User; token: string }>) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      })
      
      // Get User Profile
      .addCase(getUserProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUserProfile.fulfilled, (state, action: PayloadAction<User>) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        const errorMessage = String(action.payload || '');
        const shouldClearAuth =
          errorMessage.includes('身份验证失败') ||
          errorMessage.includes('未找到token');

        state.isLoading = false;
        state.error = action.payload as string;

        if (shouldClearAuth) {
          // 仅在明确鉴权失败时清理会话
          state.isAuthenticated = false;
          state.user = null;
          state.token = null;
          localStorage.removeItem('token');
        }
      });
  },
});

export const { clearError } = authSlice.actions;
export default authSlice.reducer;
